import React from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../../hooks/redux'
import { type RootState } from '../../../store/store'
import {
    buildBsoUnitAnalyticsRows,
    formatUnitReviewReasonLabels,
    isManualBsoAction,
    normalizeUnitReview,
    type BsoUnitAnalyticsRow,
    type BsoUnitReview,
    type EntityBreakdownSummary
} from './adminBsoUnitStatistics'
import {
    buildActivitySummary,
    buildAdvertiseRoute,
    buildOfferRoute,
    buildViewingRoute,
    filterActivityItems,
    formatActivityDate,
    formatActivityMoney,
    getActivityFilterOptions,
    getAdvertisementLinks,
    getAgentDisplay,
    getOfferAmountDetails,
    getOfferClientDetails,
    getOfferContractDetails,
    getOfferDocumentFields,
    getOfferDocuments,
    getOfferNegotiationNotes,
    getOfferType,
    getStatusBadgeClassName,
    getUnitAdvertisementsFromStore,
    getUnitOffersFromStore,
    getUnitViewingsFromStore,
    getViewingFeedbackSummary,
    getViewingNotifyDisplay,
    getViewingRescheduleReason,
    getViewingVisitorDisplay,
    sortNewestFirst,
    type ActivityFilterKey,
    type UnitActivityTab
} from './adminBsoUnitActivity'
import { ManualAdvertisementPortalLinksModal } from './manualOperations/ManualAdvertisementPortalLinksModal'
import './AdminBsoUnitActivityPage.scss'

const EmptyValue = (): JSX.Element => <span className="text-muted">—</span>

const TABS: Array<{ key: UnitActivityTab, label: string }> = [
    { key: 'advertisements', label: 'Advertisements' },
    { key: 'viewings', label: 'Viewings' },
    { key: 'offers', label: 'Offers' }
]

const normalizeTab = (value: string | null): UnitActivityTab =>
    value === 'viewings' || value === 'offers' ? value : 'advertisements'

const getAttributes = (item: any): any => item?.attributes ?? item ?? {}

const StatusBadge = ({ status, item }: { status: string, item?: any }): JSX.Element => (
    <span className={`badge ${getStatusBadgeClassName(status, item)} bso-unit-activity__status-badge`}>{status}</span>
)

const StatusBadges = ({ statuses, item }: { statuses: string[], item?: any }): JSX.Element => {
    if (statuses.length === 0) return <EmptyValue />
    return (
        <div className="bso-unit-activity__badges">
            {statuses.map((status) => (
                <StatusBadge status={status} item={item} key={status} />
            ))}
        </div>
    )
}

const ManualBsoBadge = (): JSX.Element => (
    <span className="bso-unit-activity__manual-bso-badge">Manual by BSO</span>
)

const UnitReviewBadge = ({ unitReview }: { unitReview: BsoUnitReview | null }): JSX.Element | null => {
    if (unitReview == null) return null
    return (
        <div className="bso-unit-activity__unit-review">
            {unitReview.temporaryReasons.length > 0 && <><span className="badge badge-light-warning">Temporary Problems</span><small>{formatUnitReviewReasonLabels(unitReview.temporaryReasons)}</small></>}
            {unitReview.permanentReasons.length > 0 && <><span className="badge badge-light-info border border-info text-info">Permanent Problems</span><small>{formatUnitReviewReasonLabels(unitReview.permanentReasons)}</small></>}
        </div>
    )
}

const SummaryCard = ({ title, summary }: { title: string, summary: EntityBreakdownSummary }): JSX.Element => {
    const primaryCount = summary.countBreakdown?.real ?? summary.total
    const testCount = summary.countBreakdown?.test ?? 0
    return (
        <article className="bso-unit-activity__summary-card">
            <span>{title}</span>
            <div className="bso-unit-activity__summary-total-line">
                <strong>{primaryCount}</strong>
                {testCount > 0 && <small className="bso-unit-activity__summary-test-count">+{testCount} test</small>}
            </div>
            <div>
                {summary.buckets
                    .filter((bucket) => bucket.count > 0)
                    .map((bucket) => (
                        <small key={bucket.key}>
                            <span>{bucket.label}</span>
                            <b>{bucket.count}</b>
                        </small>
                    ))}
                {(summary.manualByBsoCount ?? 0) > 0 && (
                    <small>
                        <span>Manual by BSO</span>
                        <b>{summary.manualByBsoCount}</b>
                    </small>
                )}
            </div>
        </article>
    )
}

const AgentCell = ({ item, users }: { item: any, users: any[] }): JSX.Element => {
    const agent = getAgentDisplay(item, users)
    return (
        <div className="bso-unit-activity__agent">
            {agent.route != null
                ? <Link to={agent.route}>{agent.name}</Link>
                : <strong>{agent.name}</strong>}
            {isManualBsoAction(item) && <ManualBsoBadge />}
            {agent.company != null && <small>{agent.company}</small>}
        </div>
    )
}

const buildQuickActionsRoute = (kind: 'advertise' | 'viewing' | 'offer', unitId: number, entityId: unknown): string | null => {
    const numericEntityId = Number(entityId)
    if (!Number.isFinite(unitId) || !Number.isFinite(numericEntityId)) return null
    return `/landlord/${kind}_actions/${String(unitId)}/${String(numericEntityId)}`
}

const RowActions = ({
    route,
    label,
    quickActionsRoute,
    onEditPortalLinks
}: {
    route: string | null
    label: string
    quickActionsRoute: string | null
    onEditPortalLinks?: () => void
}): JSX.Element => (
    <div className="bso-unit-activity__row-actions">
        {route != null && <Link className="btn btn-sm btn-light bso-unit-activity__inline-action" to={route}>Open {label}</Link>}
        {quickActionsRoute != null && <Link className="btn btn-sm btn-light bso-unit-activity__inline-action" to={quickActionsRoute}>Quick Actions</Link>}
        {onEditPortalLinks != null && (
            <button className="btn btn-sm btn-light bso-unit-activity__inline-action" type="button" onClick={onEditPortalLinks}>
                Edit Portal Links
            </button>
        )}
    </div>
)

const LabeledValue = ({ label, value, strong = false }: { label: string, value: React.ReactNode, strong?: boolean }): JSX.Element => (
    <span className="bso-unit-activity__kv">
        <small>{label}</small>
        {strong ? <strong>{value}</strong> : <b>{value}</b>}
    </span>
)

const FilterToggle = ({
    summary,
    value,
    onChange
}: {
    summary: EntityBreakdownSummary
    value: ActivityFilterKey
    onChange: (value: ActivityFilterKey) => void
}): JSX.Element => (
    <div className="bso-unit-activity__filters" role="group" aria-label="Activity status filter">
        {getActivityFilterOptions(summary).map((option) => (
            <button
                className={value === option.key ? 'active' : ''}
                key={option.key}
                type="button"
                onClick={() => { onChange(option.key) }}
            >
                {option.label}
                <span>{option.count}</span>
            </button>
        ))}
    </div>
)

const ActivityTableShell = ({
    title,
    count,
    children
}: {
    title: string
    count: number
    children: React.ReactNode
}): JSX.Element => (
    <section className="card bso-unit-activity__section">
        <div className="bso-unit-activity__section-head">
            <div>
                <h2>{title}</h2>
                <p>{count} item{count === 1 ? '' : 's'}</p>
            </div>
        </div>
        {children}
    </section>
)

const EmptyState = ({ label }: { label: string }): JSX.Element => (
    <div className="bso-unit-activity__empty">No {label} activity for this unit.</div>
)

const AdvertisementLinks = ({ advertise }: { advertise: any }): JSX.Element => {
    const links = getAdvertisementLinks(advertise)
    if (links.length === 0) return <EmptyValue />
    return (
        <div className="bso-unit-activity__links bso-unit-activity__links--portal">
            {links.map((link, index) => (
                <div className="bso-unit-activity__link-row" key={`${link.label}-${String(link.trakheesiId ?? '')}-${String(index)}`}>
                    {link.trakheesiId != null && (
                        <span className="bso-unit-activity__trakheesi-value">
                            <small>Trakheesi</small>
                            <b>{link.trakheesiId}</b>
                        </span>
                    )}
                    {link.url != null
                        ? <a href={link.url} target="_blank" rel="noreferrer">Link: {link.label}</a>
                        : link.trakheesiId == null && <span>Link: {link.label}</span>}
                    {link.note != null && <small>{link.note}</small>}
                </div>
            ))}
        </div>
    )
}

const AdvertisementsTable = ({
    items,
    summary,
    filter,
    users,
    unitId,
    onFilterChange,
    onEditPortalLinks
}: {
    items: any[]
    summary: EntityBreakdownSummary
    filter: ActivityFilterKey
    users: any[]
    unitId: number
    onFilterChange: (value: ActivityFilterKey) => void
    onEditPortalLinks: (advertise: any) => void
}): JSX.Element => {
    const filteredItems = filterActivityItems('advertisements', items, filter)

    return (
        <ActivityTableShell title="Advertisements" count={filteredItems.length}>
            <FilterToggle summary={summary} value={filter} onChange={onFilterChange} />
            {filteredItems.length === 0
                ? <EmptyState label="advertisement" />
                : (
                    <div className="bso-unit-activity__table-wrap">
                        <table className="table align-middle mb-0 bso-unit-activity__table">
                            <thead>
                                <tr className="text-muted fw-semibold">
                                    <th>Status</th>
                                    <th>Agent</th>
                                    <th>Start / End</th>
                                    <th>Duration / Credits</th>
                                    <th>Portal Links / Trakheesi</th>
                                    <th>Reject reason / Notes</th>
                                    <th>Quick Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((advertise) => {
                                    const attributes = getAttributes(advertise)
                                    const route = buildAdvertiseRoute(advertise)
                                    return (
                                        <tr key={advertise.id}>
                                            <td>
                                                <StatusBadges statuses={Array.isArray(attributes?.Statuses) ? attributes.Statuses : []} item={advertise} />
                                            </td>
                                            <td><AgentCell item={advertise} users={users} /></td>
                                            <td>
                                                <div className="bso-unit-activity__stack">
                                                    <LabeledValue label="Start" value={formatActivityDate(attributes?.StartDate)} strong />
                                                    <LabeledValue label="End" value={formatActivityDate(attributes?.EndDate)} strong />
                                                </div>
                                            </td>
                                            <td>
                                                <div className="bso-unit-activity__stack">
                                                    <LabeledValue label="Duration" value={attributes?.Duration ?? <EmptyValue />} strong />
                                                    <LabeledValue label="Credits" value={attributes?.CreditsAmount ?? <EmptyValue />} strong />
                                                </div>
                                            </td>
                                            <td><AdvertisementLinks advertise={advertise} /></td>
                                            <td>
                                                {attributes?.RejectReason != null && attributes.RejectReason !== ''
                                                    ? <span>{attributes.RejectReason}</span>
                                                    : <EmptyValue />}
                                            </td>
                                            <td>
                                                <RowActions route={route} label={`ad #${String(advertise.id)}`} quickActionsRoute={buildQuickActionsRoute('advertise', unitId, advertise.id)} onEditPortalLinks={() => onEditPortalLinks(advertise)} />
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
        </ActivityTableShell>
    )
}

const ViewingsTable = ({
    items,
    summary,
    filter,
    users,
    unitId,
    onFilterChange
}: {
    items: any[]
    summary: EntityBreakdownSummary
    filter: ActivityFilterKey
    users: any[]
    unitId: number
    onFilterChange: (value: ActivityFilterKey) => void
}): JSX.Element => {
    const filteredItems = filterActivityItems('viewings', items, filter)

    return (
        <ActivityTableShell title="Viewings" count={filteredItems.length}>
            <FilterToggle summary={summary} value={filter} onChange={onFilterChange} />
            {filteredItems.length === 0
                ? <EmptyState label="viewing" />
                : (
                    <div className="bso-unit-activity__table-wrap">
                        <table className="table align-middle mb-0 bso-unit-activity__table">
                            <thead>
                                <tr className="text-muted fw-semibold">
                                    <th>Status</th>
                                    <th>Agent</th>
                                    <th>Viewing Date</th>
                                    <th>Visitor / Notify</th>
                                    <th>Feedback / Complaint</th>
                                    <th>Reject / Reschedule reason</th>
                                    <th>Quick Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((viewing) => {
                                    const attributes = getAttributes(viewing)
                                    const route = buildViewingRoute(viewing)
                                    const feedback = getViewingFeedbackSummary(attributes?.Feedback)
                                    const visitor = getViewingVisitorDisplay(viewing)
                                    const rescheduleReason = getViewingRescheduleReason(viewing)
                                    return (
                                        <tr key={viewing.id}>
                                            <td>
                                                <StatusBadges statuses={Array.isArray(attributes?.Statuses) ? attributes.Statuses : []} item={viewing} />
                                            </td>
                                            <td><AgentCell item={viewing} users={users} /></td>
                                            <td><strong>{formatActivityDate(attributes?.Datetime ?? attributes?.createdAt, true)}</strong></td>
                                            <td>
                                                {visitor != null ? <strong>{visitor}</strong> : <EmptyValue />}
                                                <small>{getViewingNotifyDisplay(attributes?.Notify)}</small>
                                            </td>
                                            <td>
                                                {feedback.primary != null ? <strong>{feedback.primary}</strong> : <EmptyValue />}
                                                {feedback.details.map((detail) => <small key={detail}>{detail}</small>)}
                                                {attributes?.Complaint != null && attributes.Complaint !== '' && <small>{attributes.Complaint}</small>}
                                            </td>
                                            <td>
                                                {attributes?.RejectReason != null && attributes.RejectReason !== ''
                                                    ? <strong>{attributes.RejectReason}</strong>
                                                    : rescheduleReason != null
                                                        ? <strong>{rescheduleReason}</strong>
                                                    : <EmptyValue />}
                                            </td>
                                            <td><RowActions route={route} label={`viewing #${String(viewing.id)}`} quickActionsRoute={buildQuickActionsRoute('viewing', unitId, viewing.id)} /></td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
        </ActivityTableShell>
    )
}

const OfferValueCell = ({ offer }: { offer: any }): JSX.Element => {
    const details = getOfferAmountDetails(offer)
    return (
        <div className="bso-unit-activity__stack">
            {details.length > 0
                ? details.map((item, index) => <LabeledValue label={item.label} value={item.value} strong={index === 0} key={item.label} />)
                : <EmptyValue />}
        </div>
    )
}

const OfferTenantCell = ({ offer }: { offer: any }): JSX.Element => {
    const offerType = getOfferType(offer)
    const name = [offerType?.FirstName, offerType?.LastName]
        .filter((value) => typeof value === 'string' && value.trim() !== '')
        .join(' ')
        .trim()
    return (
        <div className="bso-unit-activity__stack">
            <strong>{name !== '' ? name : '-'}</strong>
            {getOfferClientDetails(offer).map((detail) => <small key={detail}>{detail}</small>)}
        </div>
    )
}

const OfferDocumentsCell = ({ offer, documentsStore }: { offer: any, documentsStore: any[] }): JSX.Element => {
    const documents = [...getOfferDocuments(offer, documentsStore), ...getOfferDocumentFields(offer)]
    if (documents.length === 0) return <EmptyValue />
    return (
        <div className="bso-unit-activity__links">
            {documents.map((document) => (
                <div className="bso-unit-activity__link-row" key={`${String(document.id)}-${document.label}-${String(document.url)}`}>
                    {document.url != null
                        ? <a href={document.url} target="_blank" rel="noreferrer">{document.label}</a>
                        : <strong>{document.label}</strong>}
                </div>
            ))}
        </div>
    )
}

const OffersTable = ({
    items,
    summary,
    filter,
    users,
    unitId,
    documentsStore,
    onFilterChange
}: {
    items: any[]
    summary: EntityBreakdownSummary
    filter: ActivityFilterKey
    users: any[]
    unitId: number
    documentsStore: any[]
    onFilterChange: (value: ActivityFilterKey) => void
}): JSX.Element => {
    const filteredItems = filterActivityItems('offers', items, filter)

    return (
        <ActivityTableShell title="Offers" count={filteredItems.length}>
            <FilterToggle summary={summary} value={filter} onChange={onFilterChange} />
            {filteredItems.length === 0
                ? <EmptyState label="offer" />
                : (
                    <div className="bso-unit-activity__table-wrap">
                        <table className="table align-middle mb-0 bso-unit-activity__table bso-unit-activity__table--offers">
                            <thead>
                                <tr className="text-muted fw-semibold">
                                    <th>Status</th>
                                    <th>Agent</th>
                                    <th>Offer</th>
                                    <th>Tenant / Client</th>
                                    <th>Contract / Process</th>
                                    <th>Negotiation / Reject / Notes</th>
                                    <th>Documents</th>
                                    <th>Quick Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((offer) => {
                                    const attributes = getAttributes(offer)
                                    const route = buildOfferRoute(offer)
                                    const contractDetails = getOfferContractDetails(offer)
                                    const notes = getOfferNegotiationNotes(offer)
                                    return (
                                        <tr key={offer.id}>
                                            <td>
                                                <StatusBadges statuses={Array.isArray(attributes?.Statuses) ? attributes.Statuses : []} item={offer} />
                                            </td>
                                            <td><AgentCell item={offer} users={users} /></td>
                                            <td><OfferValueCell offer={offer} /></td>
                                            <td><OfferTenantCell offer={offer} /></td>
                                            <td>
                                                <div className="bso-unit-activity__stack">
                                                    {contractDetails.length > 0
                                                        ? contractDetails.map((item) => <LabeledValue label={item.label} value={item.value} strong key={item.label} />)
                                                        : <EmptyValue />}
                                                </div>
                                            </td>
                                            <td>
                                                {notes.length > 0
                                                    ? (
                                                        <div className="bso-unit-activity__stack">
                                                            {notes.map((note, index) => index === 0 ? <strong key={note}>{note}</strong> : <small key={note}>{note}</small>)}
                                                        </div>
                                                    )
                                                    : <EmptyValue />}
                                            </td>
                                            <td><OfferDocumentsCell offer={offer} documentsStore={documentsStore} /></td>
                                            <td><RowActions route={route} label={`offer #${String(offer.id)}`} quickActionsRoute={buildQuickActionsRoute('offer', unitId, offer.id)} /></td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
        </ActivityTableShell>
    )
}

export default function AdminBsoUnitActivityPage(): JSX.Element {
    const { unitId: unitIdParam } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const location = useLocation()
    const unitId = Number(unitIdParam)
    const activeTab = normalizeTab(searchParams.get('tab'))

    const units = useAppSelector((state: RootState) => state.units.units)
    const offers = useAppSelector((state: RootState) => state.offers.offers)
    const viewings = useAppSelector((state: RootState) => state.viewings.viewings)
    const advertises = useAppSelector((state: RootState) => state.advertises.advertises)
    const users = useAppSelector((state: RootState) => state.user.allUsers)
    const documents = useAppSelector((state: RootState) => state.documents.documents)

    const unit = React.useMemo(() => units.find((candidate) => Number(candidate?.id) === unitId), [unitId, units])
    const analyticsRow = React.useMemo<BsoUnitAnalyticsRow | null>(() => {
        if (unit == null) return null
        return buildBsoUnitAnalyticsRows(units, offers, viewings, advertises).find((row) => row.unitId === unitId) ?? null
    }, [advertises, offers, unit, unitId, units, viewings])

    const unitAttributes = getAttributes(unit)
    const unitReview = analyticsRow?.unitReview ?? normalizeUnitReview(unitAttributes?.UnitReview)
    const advertisements = React.useMemo(() => unit == null ? [] : sortNewestFirst(getUnitAdvertisementsFromStore(unit, advertises), ['createdAt', 'StartDate']), [advertises, unit])
    const unitViewings = React.useMemo(() => unit == null ? [] : sortNewestFirst(getUnitViewingsFromStore(unit, viewings), ['Datetime', 'createdAt']), [unit, viewings])
    const unitOffers = React.useMemo(() => unit == null ? [] : sortNewestFirst(getUnitOffersFromStore(unit, offers), ['createdAt', 'ProcessStartDate']), [offers, unit])

    const advertisementSummary = React.useMemo(() => buildActivitySummary('advertisements', advertisements, new Date(), analyticsRow?.isDevUnit === true), [advertisements, analyticsRow?.isDevUnit])
    const viewingSummary = React.useMemo(() => buildActivitySummary('viewings', unitViewings, new Date(), analyticsRow?.isDevUnit === true), [analyticsRow?.isDevUnit, unitViewings])
    const offerSummary = React.useMemo(() => buildActivitySummary('offers', unitOffers, new Date(), analyticsRow?.isDevUnit === true), [analyticsRow?.isDevUnit, unitOffers])

    const [advertisementFilter, setAdvertisementFilter] = React.useState<ActivityFilterKey>('all')
    const [viewingFilter, setViewingFilter] = React.useState<ActivityFilterKey>('all')
    const [offerFilter, setOfferFilter] = React.useState<ActivityFilterKey>('all')
    const [editingPortalLinksAdvertise, setEditingPortalLinksAdvertise] = React.useState<any | null>(null)

    const setTab = React.useCallback((tab: UnitActivityTab) => {
        setSearchParams({ tab })
    }, [setSearchParams])

    const goBack = React.useCallback(() => {
        if ((location.state as { fromBsoStatistics?: boolean } | null)?.fromBsoStatistics === true) {
            navigate(-1)
            return
        }
        navigate('/landlord/my_units/statistics')
    }, [location.state, navigate])

    if (!Number.isFinite(unitId) || unit == null || analyticsRow == null) {
        return (
            <div className="bso-unit-activity">
                <div className="bso-unit-activity__container">
                    <header className="bso-unit-activity__header">
                        <button className="btn btn-light" type="button" onClick={goBack}>Back to BSO Unit Statistics</button>
                    </header>
                    <div className="card bso-unit-activity__section">
                        <h2>Unit activity unavailable</h2>
                        <p className="text-muted mb-0">This unit is not present in the loaded BSO statistics data.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="bso-unit-activity">
            <div className="bso-unit-activity__container">
                <header className="bso-unit-activity__header">
                    <button className="btn btn-light" type="button" onClick={goBack}>Back to BSO Unit Statistics</button>
                    <div className="bso-unit-activity__hero">
                        <img src={analyticsRow.imageUrl} alt={analyticsRow.headline} />
                        <div className="bso-unit-activity__hero-main">
                            <div className="bso-unit-activity__hero-title">
                                <h1>{analyticsRow.headline}</h1>
                                <div className="bso-unit-activity__hero-badges">
                                    {analyticsRow.marketStatus != null && <span className="badge badge-light">{analyticsRow.marketStatus}</span>}
                                    {analyticsRow.listingType != null && <span className="badge badge-light-info border border-info text-info">{analyticsRow.listingType}</span>}
                                    {analyticsRow.isDevUnit && <span className="badge badge-light-warning bso-unit-activity__test-unit-badge">Test Unit</span>}
                                </div>
                            </div>
                            <p>
                                {analyticsRow.propertyName} / {analyticsRow.areaName} / {analyticsRow.unitNumber != null ? `Unit ${analyticsRow.unitNumber}` : `Ref ${analyticsRow.unitReference}`}
                            </p>
                            <div className="bso-unit-activity__hero-meta">
                                <span>{formatActivityMoney(analyticsRow.currentPrice)}</span>
                                {analyticsRow.unitType != null && <span>{analyticsRow.unitType}</span>}
                                {analyticsRow.unitCategory != null && <span>{analyticsRow.unitCategory}</span>}
                                {analyticsRow.sizeArea != null && <span>{analyticsRow.sizeArea.toLocaleString('en-US')} sqft</span>}
                                {analyticsRow.roomsLabel != null && <span>{analyticsRow.roomsLabel}</span>}
                            </div>
                            <UnitReviewBadge unitReview={unitReview} />
                        </div>
                    </div>
                </header>

                <div className="bso-unit-activity__summary-grid">
                    <SummaryCard title="Advertisements" summary={advertisementSummary} />
                    <SummaryCard title="Viewings" summary={viewingSummary} />
                    <SummaryCard title="Offers" summary={offerSummary} />
                </div>

                <nav className="bso-unit-activity__tabs" aria-label="Unit activity tabs">
                    {TABS.map((tab) => (
                        <button
                            className={activeTab === tab.key ? 'active' : ''}
                            key={tab.key}
                            type="button"
                            onClick={() => { setTab(tab.key) }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>

                {activeTab === 'advertisements' && (
                    <AdvertisementsTable items={advertisements} summary={advertisementSummary} filter={advertisementFilter} users={users} unitId={unitId} onFilterChange={setAdvertisementFilter} onEditPortalLinks={setEditingPortalLinksAdvertise} />
                )}

                {activeTab === 'viewings' && (
                    <ViewingsTable items={unitViewings} summary={viewingSummary} filter={viewingFilter} users={users} unitId={unitId} onFilterChange={setViewingFilter} />
                )}

                {activeTab === 'offers' && (
                    <OffersTable items={unitOffers} summary={offerSummary} filter={offerFilter} users={users} unitId={unitId} documentsStore={documents} onFilterChange={setOfferFilter} />
                )}
            </div>
            <ManualAdvertisementPortalLinksModal advertise={editingPortalLinksAdvertise} onClose={() => setEditingPortalLinksAdvertise(null)} />
        </div>
    )
}
